import Vue from 'vue';
import Bugsnag from '@bugsnag/js';
import { errorCodes } from './apiErrorCodes';
import { EntityAlreadyExistsError } from 'GlobalErrors/EntityAlreadyExistsError';
import { EntityNotFoundInCacheError } from 'GlobalErrors/EntityNotFoundInCacheError';
import { StripeError } from 'GlobalErrors/StripeError';
import { InvalidPasswordError } from 'GlobalErrors/InvalidPasswordError';
import { InvalidPortalRequestedException } from 'GlobalErrors/InvalidPortalRequestException';
import { InvalidHashkeyRequested } from '../errors/InvalidHashkeyRequested';
import { EmailAlreadyVerifiedError } from '../errors/EmailAlreadyVerifiedError';
import { EntityDoesNotExistException } from '../errors/EntityDoesNotExistException';
import { CampaignStatusUpdateError } from '../errors/CampaignStatusUpdateError';
import { InvalidAccessTokenError } from '../errors/InvalidAccessTokenError';
import { InvalidLoginCredentials } from 'GlobalErrors/InvalidLoginCredentials';
import { ForbiddenPortalRequested } from 'GlobalErrors/ForbiddenPortalRequested';
import { InvalidRequestDataException } from '../errors/InvalidRequestDataException';
import { HtmlWebClientException } from 'GlobalErrors/HtmlWebClientException';
import { OtpAlreadyGeneratedException } from 'GlobalErrors/OtpAlreadyGeneratedException';
import { InvalidOtpException } from 'GlobalErrors/InvalidOtpException';
import { OtpVerificationAttemptsExceededException } from 'GlobalErrors/OtpVerificationAttemptsExceededException';
import { UnsplashApiFailureException } from 'GlobalErrors/UnsplashApiFailureException';
import Router from '../router/bizgain-router';
import { ForgotPasswordRouteObjects } from '../features/forgotPassword/router';
import { UserAccountExpiredException } from 'GlobalErrors/UserAccountExpiredException';
import { i18n } from '@/lang/';
import { PageNotFoundRoute } from '../router/pageNotFound';
import { FacebookPageAlreadyRequestedException } from 'GlobalErrors/FacebookPageAlreadyRequestedException';
import { store } from 'Store/store';
import { PasswordExpiredError } from '../errors/PasswordExpiredError';
import { OldPasswordException } from '../errors/OldPasswordException';
import { WeakPasswordException } from '../errors/WeakPasswordException';
import { TokenMalformedException } from '../errors/TokenMalformedException';

var notifyError = function (error) {
  if (error && error.response && error.response.data && error.response.data.message) {
    Bugsnag.notify(new Error(error.response.data.message));
  } else {
    Bugsnag.notify(error);
  }

  if (store.getters.isForBizgain) {
    let errors = {
      type: 'error',
      message: i18n.tc('message.somethingWentWrong')
    };
    store.dispatch('updateMessage', errors);
  } else {
    Vue.notify({
      group: 'transientErrors',
      type: 'error',
      text: i18n.tc('message.somethingWentWrong')
    });
  }
};

const unauthenticatedApiEndpoints = ['/v1.0/portals'];

export function handleError(error) {
  if (
    error &&
        error.response &&
        (error.response.data ||
            error.response.status === errorCodes.Unauthorized)
  ) {
    var errorCode =
            error.response.data.errorCode === undefined
              ? error.response.status
              : error.response.data.errorCode;
    switch (errorCode) {
      case errorCodes.EntityAlreadyExistsError:
        return Promise.reject(new EntityAlreadyExistsError(error)); // do not notify error in case of error code 101
      case errorCodes.StripeException:
        return Promise.reject(
          new StripeError(error.response.data.message)
        );
      case errorCodes.InvalidRequestDataException:
        return Promise.reject(new InvalidRequestDataException(error));
      case errorCodes.InvalidHashkeyRequested:
        return Promise.reject(new InvalidHashkeyRequested(error));
      case errorCodes.OtpAlreadyGeneratedException:
        return Promise.reject(new OtpAlreadyGeneratedException(error));
      case errorCodes.InvalidOtpException:
        return Promise.reject(new InvalidOtpException(error));
      case errorCodes.OtpVerificationAttemptsExceededException:
        return Promise.reject(new OtpVerificationAttemptsExceededException(error));
      case errorCodes.FacebookPageAlreadyRequestedException:
        return Promise.reject(new FacebookPageAlreadyRequestedException(error));
      case errorCodes.EmailAlreadyVerifiedError:
        return Promise.reject(new EmailAlreadyVerifiedError(error));
      case errorCodes.EntityDoesNotExistException:
        return Promise.reject(new EntityDoesNotExistException(error));
      case errorCodes.UserAccountExpiredException:
        return Promise.reject(new UserAccountExpiredException(error));
      case errorCodes.HtmlWebClientException:
        return Promise.reject(new HtmlWebClientException(error));
      case errorCodes.InvalidPortalRequestedException:
        if (
          Router.currentRoute.name ===
                    ForgotPasswordRouteObjects.forgotPassword.name
        ) {
          return Promise.reject(
            new InvalidPortalRequestedException(error)
          );
        }
        else {
          invalidRoute(error);
        }
        return Promise.reject();
      case errorCodes.InvalidLoginCredentials:
        if (Router.currentRoute.name === 'login') {
          return Promise.reject(new InvalidLoginCredentials(error));
        } else {
          invalidRoute(error);
        }
        return Promise.reject;
      case errorCodes.ForbiddenPortalRequested:
        if (Router.currentRoute.name === 'login') {
          return Promise.reject(new ForbiddenPortalRequested(error));
        } else {
          invalidRoute(error);
        }
        return Promise.reject;
      case errorCodes.ForbiddenBusinessProfileRequested:
      case errorCodes.InvalidBusinessProfileRequested:
      case errorCodes.Unauthorized:
      case errorCodes.TokenInvalidException:
      case errorCodes.TokenExpiredException:
      case errorCodes.RefreshTokenExpiredException:
        if (Router.currentRoute.name === 'login') {
        // this includes the API calls made in rootlayout
        // therefore, since its going to login page anyway, we are returning success here so that it doesn't throw any notification
          Promise.resolve()
        } else {
          invalidRoute(error);
        }
        return Promise.reject(new InvalidAccessTokenError(error));
      case errorCodes.TokenMalformedException:
        if (Router.currentRoute.name == 'resetPassword') {
          return Promise.reject(new TokenMalformedException(error));
        } else {
          if (Router.currentRoute.name != 'login') {
            invalidRoute(error);
          }
          return Promise.reject(new InvalidAccessTokenError(error));
        }
      case errorCodes.EntityNotFoundInCacheException:
        return Promise.reject(new EntityNotFoundInCacheError(error));
      case errorCodes.MediaPlanStatusUpdateFailedException:
      case errorCodes.NoCampaignsLinkedException:
        return Promise.reject(new CampaignStatusUpdateError(error));
      case errorCodes.UnsplashApiException:
        return Promise.reject(new UnsplashApiFailureException(error));
      case errorCodes.PasswordExpiredException:
        return Promise.reject(new PasswordExpiredError(error));
      case errorCodes.OldPasswordException:
        return Promise.reject(new OldPasswordException(error));
      case errorCodes.WeakPasswordException:
        return Promise.reject(new WeakPasswordException(error));
      default:
        notifyError(error);
        return Promise.reject(error);
    }
  } else if (error) {
    notifyError(error);
    return Promise.reject(error);
  }
}

function invalidRoute(error) {
  let requestUrl = error && error.config && error.config.url;
  if(requestUrl && unauthenticatedApiEndpoints.includes(requestUrl)){
    // fallback to avoid indefinite redirect
    Router.push({
      name: PageNotFoundRoute.name,
      params: [Router.currentRoute.path], // https://github.com/vuejs/vue-router/issues/724#issuecomment-663315290
      replace: true
    });
    return;
  }
  localStorage.removeItem('accessToken');
  localStorage.removeItem('user');
  localStorage.removeItem('pushNotificationToken');
  Router.push({
    name: 'login',
    query: {
      redirect: Router.currentRoute.path
    }
  });
}
